import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

export const usePaymentProcessor = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [processingPayment, setProcessingPayment] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Map price IDs to plan types
  const priceIdToPlanMap = {
    "price_1QvOaiG3RRUf49cQgmkP8MtT": "monthly",
    "price_1QvObYG3RRUf49cQp6XPRkNN": "yearly", 
    "price_1QvOcGG3RRUf49cQlU0O22em": "lifetime"
  };

  // Function to handle successful payment
  const handlePaymentSuccess = async (paymentMethodId: string, selectedPlan: string | null) => {
    if (!selectedPlan) {
      toast({
        title: "Error",
        description: "No plan selected",
        variant: "destructive",
      });
      return;
    }
    
    setProcessingPayment(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('You must be logged in to subscribe');
      }

      // Get plan type from price ID or use it directly if it's already a plan type
      const planType = priceIdToPlanMap[selectedPlan] || selectedPlan;

      // Check if a subscription already exists and if it's different from the selected plan.
      // Make sure to include stripe_subscription_id in the query
      const { data: currentSubscription, error: currentSubError } = await supabase
          .from('subscriptions')
          .select('id, status, plan_type, stripe_subscription_id')
          .eq('user_id', session.user.id)
          .maybeSingle();

      if (currentSubError) {
        console.error('Error fetching current subscription:', currentSubError);
      }

      let action = 'create';
      let subscriptionId = undefined;
      
      if (
        currentSubscription &&
        (currentSubscription.status === 'active' || currentSubscription.status === 'trialing')
      ) {
        action = 'upgrade';
        subscriptionId = currentSubscription.stripe_subscription_id;
      }

      console.log(`${action} subscription with payment method:`, paymentMethodId);
      console.log('Using plan type:', planType);
      
      const { data, error } = await supabase.functions.invoke('create-subscription', {
        body: {
          action,
          userId: session.user.id,
          planType,
          priceId: selectedPlan,
          paymentMethodId,
          subscriptionId: currentSubscription?.stripe_subscription_id || 'unknown'
        },
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to create subscription');
      }

      if (!data?.success) {
        console.error('Subscription response:', data);
        throw new Error('Subscription creation failed');
      }

      // Show redirecting state first
      setRedirecting(true);

      // Send subscription confirmation email
      try {
        await supabase.functions.invoke('send-notification-email', {
          body: {
            userId: session.user.id,
            type: 'subscription_created',
            data: {
              planType
            }
          }
        });
      } catch (emailError) {
        console.error('Error sending confirmation email:', emailError);
        // Non-critical, continue without interruption
      }

      // Send payment receipt email
      try {
        await supabase.functions.invoke('send-notification-email', {
          body: {
            userId: session.user.id,
            type: 'payment_receipt',
            data: {
              planType,
              amount: planType === 'monthly' ? 9.99 : planType === 'yearly' ? 59.88 : 199.99,
              currency: '$',
              paymentDate: new Date().toISOString()
            }
          }
        });
      } catch (emailError) {
        console.error('Error sending receipt email:', emailError);
        // Non-critical, continue without interruption
      }

      toast({
        title: "Success!",
        description: "Your subscription has been activated",
      });

      // Wait a moment to ensure database updates have propagated
      setTimeout(() => {
        // Directly redirect instead of depending on checkSubscriptionStatus
        console.log('Redirecting to dashboard after successful payment');
        navigate('/dashboard', { replace: true });
      }, 2000);
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process subscription",
        variant: "destructive",
      });
      setProcessingPayment(false);
      setRedirecting(false);
    }
  };

  // Helper function to check subscription status before redirecting
  const checkSubscriptionStatus = async (userId: string) => {
    console.log('Checking subscription status before redirecting...');
    let attempts = 0;
    const maxAttempts = 3;
    
    const checkStatus = async () => {
      attempts++;
      try {
        // First check our database
        const { data: subscription, error } = await supabase
          .from('subscriptions')
          .select('id, status, plan_type')
          .eq('user_id', userId)
          .maybeSingle();
          
        if (error) {
          console.error('Error checking subscription in database:', error);
        }
        
        if (subscription && 
            (subscription.status === 'active' || 
             subscription.status === 'trialing' ||
             (subscription.plan_type === 'lifetime' && subscription.status !== 'canceled'))) {
          console.log('Active subscription confirmed, redirecting to dashboard');
          // Redirect to dashboard with replace:true to prevent back button issues
          navigate('/dashboard', { replace: true });
          return;
        }
        
        // If not found in database, verify with Stripe
        console.log('Verifying subscription with Stripe...');
        const { data: stripeVerification, error: stripeError } = await supabase.functions.invoke('verify-stripe-subscription', {
          body: { userId }
        });
        
        if (stripeError) {
          console.error('Error verifying subscription with Stripe:', stripeError);
        }
        
        if (stripeVerification && (stripeVerification.hasActiveSubscription || stripeVerification.message === "Recent payment found")) {
          console.log('Stripe verification successful, redirecting to dashboard');
          navigate('/dashboard', { replace: true });
          return;
        }
        
        if (attempts < maxAttempts) {
          console.log(`Subscription not yet confirmed, retrying (${attempts}/${maxAttempts})...`);
          // Wait before trying again
          setTimeout(checkStatus, 1500);
        } else {
          console.log('Max check attempts reached, redirecting anyway');
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        console.error('Error in subscription check:', error);
        // On error, redirect anyway after max attempts
        if (attempts >= maxAttempts) {
          navigate('/dashboard', { replace: true });
        } else {
          setTimeout(checkStatus, 1500);
        }
      }
    };
    
    // Start checking
    await checkStatus();
  };

  // Function to handle payment errors
  const handlePaymentError = (error: Error) => {
    console.error('Payment error:', error);
    toast({
      title: "Payment Error",
      description: error.message,
      variant: "destructive",
    });
    setProcessingPayment(false);
  };

  // Function to handle invitation code redemption
  const handleRedeemCode = async (invitationCode: string, setRedeemingCode: (value: boolean) => void) => {
    if (!invitationCode.trim()) {
      toast({
        title: "Error",
        description: "Please enter an invitation code",
        variant: "destructive",
      });
      return;
    }

    setRedeemingCode(true);
    try {
      const { data, error } = await supabase.functions.invoke('redeem-invitation', {
        body: { code: invitationCode.trim() }
      });

      if (error) throw error;

      if (data.success) {
        const message = data.isTrial
          ? `Your 14-day trial has been activated! It will expire on ${format(new Date(data.trialEndsAt), 'MMMM dd, yyyy')}.`
          : "Your invitation code has been redeemed and your account has been activated";

        // Send subscription confirmation email
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await supabase.functions.invoke('send-notification-email', {
            body: {
              userId: session.user.id,
              type: 'subscription_created',
              data: {
                planType: data.isTrial ? 'trial' : 'lifetime',
                trialEndsAt: data.isTrial ? data.trialEndsAt : null
              }
            }
          });
        }

        toast({
          title: "Success!",
          description: message,
        });
        
        setRedirecting(true);
        
        // Directly redirect instead of waiting for database updates
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error redeeming code:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to redeem invitation code",
        variant: "destructive",
      });
    } finally {
      setRedeemingCode(false);
    }
  };

  return {
    processingPayment,
    redirecting,
    handlePaymentSuccess,
    handlePaymentError,
    handleRedeemCode
  };
};
